// Spec 083 — o contrato do RELATÓRIO de acompanhamento da produção.
//
// Um endpoint devolve totais E agrupamentos na mesma resposta, e os totais são a SOMA dos
// agrupamentos (FR-026). Duas chamadas com chaves de cache distintas poderiam dessincronizar num
// refetch parcial, e "o total não bate com a grade logo abaixo" é a falha mais provável desta tela.
//
// ⚠️ DATAS CIVIS, não instantes. O período é `YYYY-MM-DD` e a borda do dia é resolvida no fuso da
// organização, no servidor. Isso alinha o agrupamento com o expediente e elimina a classe de bug de
// meia-noite que custou 40% do volume na Spec 082.
import { z } from "zod";
import { LossTypeSchema, UuidSchema } from "./common.js";

/** `YYYY-MM-DD`. Data civil, sem hora e sem fuso — o fuso mora na organização. */
export const DateOnlySchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, "Use uma data no formato AAAA-MM-DD.");
export type DateOnly = z.infer<typeof DateOnlySchema>;

/**
 * Os tetos do relatório. Moram no contrato, e não em env nem em política da organização, porque
 * precisam ser a MESMA verdade na tela (que não deve oferecer o que será recusado) e na rota (que
 * recusa de verdade). Foi duplicar um número desses em dois lugares que custou caro na Spec 079.
 */
export const REPORT_MAX_RANGE_DAYS = 400;
/** Acima disto o agrupamento diário sai da lista: 400 linhas de grade não servem a ninguém. */
export const REPORT_DAILY_GROUPING_MAX_DAYS = 90;

export const ReportGroupBySchema = z.enum(["day", "week", "month"]);
export type ReportGroupBy = z.infer<typeof ReportGroupBySchema>;

/** `custom` usa o par `from`/`to` como veio; os demais resolvem para um par no fuso da organização. */
export const ReportPresetSchema = z.enum([
  "7d",
  "30d",
  "thisMonth",
  "lastMonth",
  "thisYear",
  "custom",
]);
export type ReportPreset = z.infer<typeof ReportPresetSchema>;

export const ReportScopeKindSchema = z.enum(["line", "chain"]);
export type ReportScopeKind = z.infer<typeof ReportScopeKindSchema>;

/**
 * Spec 084 — a CHAVE de um valor de característica, como ela viaja na URL e sai do SQL.
 *
 * `v:<uuid>` para valor de lista, `t:<texto>` para texto livre, mais os dois estados que não são
 * valor: `NONE` (nenhuma origem declarou) e `MIXED` (as origens discordam). Os dois estados são
 * recortáveis de propósito — "quanto rendeu o que ninguém marcou" é uma pergunta legítima, e some
 * da tela se ela só oferecer os valores.
 */
export const CharacteristicValueKeySchema = z
  .string()
  .min(1)
  .max(210)
  .regex(/^(v:[0-9a-fA-F-]{36}|t:.+|NONE|MIXED)$/, "Valor de recorte inválido.");

/**
 * O teto de 400 dias e o corte de 90 dias para o agrupamento diário NÃO entram como `.refine`:
 * eles têm mensagem própria e viram 422 na rota, com o texto que o gestor lê. Aqui fica só o que é
 * forma: datas válidas, ordenadas e coerentes entre si.
 */
export const ProductionReportQuerySchema = z
  .object({
    scope: ReportScopeKindSchema,
    lineId: UuidSchema,
    from: DateOnlySchema,
    to: DateOnlySchema,
    groupBy: ReportGroupBySchema,
    // ⚠️ SEM `indicatorId`. A resposta traz TODOS os indicadores da etapa e a eleição do que fica em
    // destaque é escolha de EXIBIÇÃO, resolvida no cliente (o parâmetro `indicator` da URL da tela).
    // Enquanto ele existia aqui, o contrato prometia um recorte por indicador que o serviço nunca
    // leu: quem o enviasse recebia a resposta inteira e acreditaria tê-la filtrado.
    // Spec 084 — o RECORTE por um valor de característica e o QUADRO comparativo. São eixos
    // distintos: o recorte estreita o relatório inteiro; o quadro abre uma característica em
    // colunas sobre o período todo.
    characteristicId: UuidSchema.optional(),
    value: CharacteristicValueKeySchema.optional(),
    compareBy: UuidSchema.optional(),
  })
  .strict()
  .refine((q) => q.to >= q.from, {
    message: "A data final deve ser igual ou posterior à inicial.",
    path: ["to"],
  })
  .refine((q) => (q.value === undefined) === (q.characteristicId === undefined), {
    message: "Informe a característica e o valor do recorte juntos.",
    path: ["value"],
  })
  .refine((q) => q.compareBy === undefined || q.compareBy !== q.characteristicId, {
    message: "O quadro comparativo precisa ser de outra característica.",
    path: ["compareBy"],
  });
export type ProductionReportQuery = z.infer<typeof ProductionReportQuerySchema>;

// --- Itens da projeção -------------------------------------------------------

export const ReportProductItemSchema = z.object({
  productId: UuidSchema,
  productName: z.string(),
  quantity: z.number().finite(),
  /** Veio de pacote convertido pela gramatura — é o que autoriza o sufixo de equivalência (FR-016). */
  converted: z.boolean(),
});
export type ReportProductItem = z.infer<typeof ReportProductItemSchema>;

export const ReportLossItemSchema = z.object({
  productId: UuidSchema.nullable(),
  productName: z.string().nullable(),
  /** Nulo quando a origem é o contador de máquina, que não declara tipo por lançamento. */
  lossType: LossTypeSchema.nullable(),
  quantity: z.number().finite(),
});
export type ReportLossItem = z.infer<typeof ReportLossItemSchema>;

export const ReportInputSchema = z.object({
  /** `null` = nenhum contador mede a entrada desta etapa. NUNCA zero (FR-021). */
  quantity: z.number().finite().nullable(),
  /** O que as etapas anteriores dizem ter produzido dos produtos que esta consome. */
  upstreamDeclared: z.number().finite().nullable(),
  upstreamLineIds: z.array(UuidSchema),
  divergence: z.number().finite().nullable(),
  divergencePct: z.number().finite().nullable(),
});
export type ReportInput = z.infer<typeof ReportInputSchema>;

export const ReportIndicatorSchema = z.object({
  id: UuidSchema,
  name: z.string(),
  baseUnit: z.string(),
  target: z.number().finite().nullable(),
  /** Percentual. NÃO capado em 100: acima disso é consumo de estoque, sinal legítimo (FR-020). */
  value: z.number().finite().nullable(),
  numerator: z.number().finite().nullable(),
  denominator: z.number().finite().nullable(),
  /** Mesmo vocabulário da Spec 082: `NO_BASE` vira "sem base" em texto, nunca `0%`. */
  reason: z.enum(["OK", "NO_BASE", "ZERO_BASE"]),
  previousValue: z.number().finite().nullable(),
  deltaPp: z.number().finite().nullable(),
  /**
   * Spec 084 (FR-025) — os dias em que houve NUMERADOR registrado e a base faltou (`absent`) ou
   * veio zero (`zero`), DENTRO de um agrupamento (nunca menor que a semana) cuja base não cobre a
   * saída. Dia sem base num agrupamento coberto é o estoque entre etapas, não falta de registro.
   * Objeto no nível da etapa; `null` no nível do agrupamento, onde a lista seria a mesma repetida.
   *
   * ⚠️ O aviso NÃO muda nenhum número. Ele diz que saiu mais do que entrou naquela janela — a
   * correção é no dado, não no indicador.
   */
  suspiciousDays: z
    .object({
      absent: z.array(z.object({ day: DateOnlySchema, numerator: z.number().finite() })),
      zero: z.array(z.object({ day: DateOnlySchema, numerator: z.number().finite() })),
    })
    .nullable(),
  /** No agrupamento: contém ao menos um dia suspeito. Na etapa: `absent ∪ zero ≠ ∅`. */
  suspicious: z.boolean(),
});
export type ReportIndicator = z.infer<typeof ReportIndicatorSchema>;

export const ReportBucketSchema = z.object({
  /** Data civil de início do agrupamento — é a chave e o rótulo. */
  key: DateOnlySchema,
  from: DateOnlySchema,
  to: DateOnlySchema,
  /** A faixa passa de `to` ou de hoje. A planilha da fábrica chama o mês corrente de parcial. */
  partial: z.boolean(),
  input: ReportInputSchema,
  products: z.array(ReportProductItemSchema),
  losses: z.array(ReportLossItemSchema),
  indicators: z.array(ReportIndicatorSchema),
});
export type ReportBucket = z.infer<typeof ReportBucketSchema>;

export const ReportStageSchema = z.object({
  lineId: UuidSchema,
  lineName: z.string(),
  /** Nível topológico na cadeia. Recorte por linha devolve sempre 0. */
  level: z.number().int().nonnegative(),
  unit: z.string(),
  unitIsEquivalent: z.boolean(),
  /** `mixed` quando o período tem execuções resolvidas pelos dois caminhos. Dado, não defeito. */
  source: z.enum(["machine", "run", "mixed"]),
  input: ReportInputSchema,
  products: z.array(ReportProductItemSchema),
  losses: z.array(ReportLossItemSchema),
  indicators: z.array(ReportIndicatorSchema),
  buckets: z.array(ReportBucketSchema),
});
export type ReportStage = z.infer<typeof ReportStageSchema>;

/**
 * Spec 084 — o recorte aplicado, devolvido pelo servidor.
 *
 * `markedRunsInPeriod` existe para a tela poder distinguir "o recorte não achou nada" de "ninguém
 * marcou batelada neste período". Zerar a tela nos dois casos diria a mesma coisa para dois
 * problemas com soluções opostas.
 */
export const ReportFilterSchema = z.object({
  characteristicId: UuidSchema,
  characteristicName: z.string(),
  valueKey: z.string(),
  /** "não informado" / "misto" quando o recorte é por um dos dois estados. */
  valueLabel: z.string(),
  excluded: z.object({ mixedRuns: z.number().int(), noneRuns: z.number().int() }),
  markedRunsInPeriod: z.number().int(),
});
export type ReportFilter = z.infer<typeof ReportFilterSchema>;

export const ReportComparisonColumnSchema = z.object({
  /** `v:…` | `t:…` | `OTHERS` | `NONE` | `MIXED`. */
  key: z.string(),
  label: z.string(),
  kind: z.enum(["VALUE", "OTHERS", "NONE", "MIXED"]),
  runs: z.number().int(),
  stages: z.array(
    z.object({
      lineId: UuidSchema,
      input: ReportInputSchema,
      products: z.array(ReportProductItemSchema),
      losses: z.array(ReportLossItemSchema),
      indicators: z.array(ReportIndicatorSchema),
    })
  ),
});
export type ReportComparisonColumn = z.infer<typeof ReportComparisonColumnSchema>;

/**
 * O quadro compara o período INTEIRO, sem o recorte (FR-015/SC-003): a soma das colunas é o período
 * sem recorte, item por item. Recortar e comparar ao mesmo tempo daria colunas que não somam nada
 * reconhecível — e é somar que prova que a classificação não perdeu lançamento.
 */
export const ReportComparisonSchema = z.object({
  characteristicId: UuidSchema,
  characteristicName: z.string(),
  columns: z.array(ReportComparisonColumnSchema),
});
export type ReportComparison = z.infer<typeof ReportComparisonSchema>;

export const ProductionReportResponseSchema = z.object({
  scope: z.object({ kind: ReportScopeKindSchema, lineIds: z.array(UuidSchema) }),
  /** Nulo sem recorte — e sem recorte a resposta é idêntica à de antes da Spec 084 (FR-017). */
  filter: ReportFilterSchema.nullable(),
  comparison: ReportComparisonSchema.nullable(),
  period: z.object({
    from: DateOnlySchema,
    to: DateOnlySchema,
    groupBy: ReportGroupBySchema,
    /** Janela anterior de MESMO número de dias — é o que alimenta `deltaPp` (FR-019). */
    previous: z.object({ from: DateOnlySchema, to: DateOnlySchema }),
  }),
  stages: z.array(ReportStageSchema),
});
export type ProductionReportResponse = z.infer<typeof ProductionReportResponseSchema>;
